/* eslint-disable no-param-reassign */
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { ResponsivePie } from "@nivo/pie";
import { Table } from "semantic-ui-react";
import _ from "lodash";

const PieGeneric = ({
  data,
  handleChartClick,
  clickType,
  graphTextColor,
  showDataTables,
  styles
}) => {
  // @nivo/pie v 0.59.1 does not have a ResponsivePie parameter to change only the pie slice inner text color,
  // so I had to add a theme for all text that contained my custom color (graphTextColor) and then set the radialLabelsTextColor="black"
  const theme = {
    labels: {
      text: {
        fontSize: 12,
        fill: graphTextColor // Arc labels (labels on the slices)
      }
    },
    legends: {
      text: {
        fontSize: 12,
        fill: "#5e5e5e" // Legend text
      }
    }
  };

  const totalRecommendations = _.sumBy(data, "value");

  const chartRef = useRef();
  const isContainerMouseFocus = useRef(false);

  // SVG Path Interception for keyboard accessibility
  useEffect(() => {
    const addAccessibilityToSVGPaths = () => {
      if (!chartRef.current) return;

      const svg = chartRef.current.querySelector("svg");
      if (!svg) return;

      const slicePaths = svg.querySelectorAll(
        'path[fill]:not([fill="none"]):not([fill="transparent"])'
      );

      slicePaths.forEach((path, index) => {
        if (index >= data.length) return;

        const sliceData = data[index];

        path.setAttribute("tabindex", "0");
        path.setAttribute("role", "button");
        path.setAttribute(
          "aria-label",
          `${sliceData.label}: ${
            sliceData.value
          } recommendations. Press Enter to filter.`
        );

        // Remove existing event listeners to avoid duplicates
        path.removeEventListener("focus", path._focusHandler);
        path.removeEventListener("blur", path._blurHandler);
        path.removeEventListener("keydown", path._keydownHandler);
        path.removeEventListener("mousedown", path._mousedownHandler);
        path.removeEventListener("click", path._clickHandler);

        // Track if focus came from mouse
        let isMouseFocus = false;

        // Mousedown handler - sets flag before focus event
        const mousedownHandler = () => {
          isMouseFocus = true;
          setTimeout(() => {
            isMouseFocus = false;
          }, 0);
        };

        // Focus handler - capture original values AT FOCUS TIME
        const focusHandler = e => {
          const originalStroke =
            e.target.getAttribute("stroke") || "transparent";
          const originalStrokeWidth =
            e.target.getAttribute("stroke-width") || "0";

          // Store on path for blur to use
          path._capturedStroke = originalStroke;
          path._capturedStrokeWidth = originalStrokeWidth;

          // Only apply styling if NOT from mouse
          if (!isMouseFocus) {
            e.target.style.stroke = "#ffffff";
            e.target.style.strokeWidth = "3px";
            e.target.style.filter =
              "drop-shadow(0 0 2px #0078d4) drop-shadow(0 0 2px #0078d4) drop-shadow(0 0 3px #0078d4)";
          }
          e.target.style.outline = "none";
        };

        // Blur handler - restore from captured values
        const blurHandler = e => {
          // Use captured values
          e.target.style.stroke = path._capturedStroke || "transparent";
          e.target.style.strokeWidth = path._capturedStrokeWidth || "0";
          e.target.style.filter = "";

          // Clean up
          delete path._capturedStroke;
          delete path._capturedStrokeWidth;
        };

        // Keydown handler
        const keydownHandler = e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleChartClick(sliceData, clickType);
          }
        };

        // Store handlers on the element for cleanup
        path._focusHandler = focusHandler;
        path._blurHandler = blurHandler;
        path._keydownHandler = keydownHandler;
        path._mousedownHandler = mousedownHandler;

        // Add event listeners
        path.addEventListener("mousedown", mousedownHandler);
        path.addEventListener("focus", focusHandler);
        path.addEventListener("blur", blurHandler);
        path.addEventListener("keydown", keydownHandler);

        path.style.cursor = "pointer";
      });
    };

    const timer = setTimeout(addAccessibilityToSVGPaths, 100);

    const observer = new MutationObserver(() => {
      clearTimeout(timer);
      setTimeout(addAccessibilityToSVGPaths, 100);
    });

    if (chartRef.current) {
      observer.observe(chartRef.current, {
        childList: true,
        subtree: true
      });
    }

    return () => {
      clearTimeout(timer);
      observer.disconnect();

      if (chartRef.current) {
        const svg = chartRef.current.querySelector("svg");
        if (svg) {
          const slicePaths = svg.querySelectorAll('path[tabindex="0"]');
          slicePaths.forEach(path => {
            if (path._focusHandler)
              path.removeEventListener("focus", path._focusHandler);
            if (path._blurHandler)
              path.removeEventListener("blur", path._blurHandler);
            if (path._keydownHandler)
              path.removeEventListener("keydown", path._keydownHandler);
            if (path._mousedownHandler)
              path.removeEventListener("mousedown", path._mousedownHandler);

            path.removeAttribute("tabindex");
            path.removeAttribute("role");
            path.removeAttribute("aria-label");

            delete path._capturedStroke;
            delete path._capturedStrokeWidth;
          });
        }
      }
    };
  }, [data, handleChartClick, clickType]);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={chartRef}
        className="reco-detailed-stats-sub-visual"
        tabIndex="0" // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
        role="img"
        aria-label={`Interactive pie chart showing ${totalRecommendations} recommendations by ${clickType}. Tab through individual slices to filter data. Press enter to populate the Recommendation List with corresponding pie slice data.`}
        style={{ outline: "none" }}
        onMouseDown={() => {
          isContainerMouseFocus.current = true;
          setTimeout(() => {
            isContainerMouseFocus.current = false;
          }, 100);
        }}
        onFocus={e => {
          if (!isContainerMouseFocus.current) {
            e.target.style.outline = "2px solid #0078d4";
          }
        }}
        onBlur={e => {
          e.target.style.outline = "none";
        }}
      >
        <ResponsivePie
          data={data}
          radialLabelsTextColor="black"
          theme={theme}
          margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
          innerRadius={0.5}
          colors={d => d.graphColor}
          animate
          motionStiffness={90}
          motionDamping={15}
          enableRadialLabels
          onClick={e => handleChartClick(e, clickType)}
        />
      </div>
      <div css={styles} className={showDataTables ? "" : "sr-only"}>
        <Table
          id={`${clickType}-pie-data-table`}
          aria-label={`Recommendations data by ${clickType}`}
          basic="very"
          celled
          compact
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                colSpan={3}
                textAlign="center"
                style={{ fontWeight: "bold" }}
                scope="col"
              >
                Detailed Breakdown of {totalRecommendations} Recommendations by{" "}
                {clickType.charAt(0).toUpperCase() + clickType.slice(1)}
              </Table.HeaderCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell scope="col">
                {clickType.charAt(0).toUpperCase() + clickType.slice(1)}
              </Table.HeaderCell>
              <Table.HeaderCell scope="col" textAlign="center">
                Count
              </Table.HeaderCell>
              <Table.HeaderCell scope="col" textAlign="center">
                Percentage
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {data.map(item => (
              <Table.Row key={item.id}>
                <Table.HeaderCell scope="row" textAlign="left">
                  {item.label}
                </Table.HeaderCell>
                <Table.Cell textAlign="center">{item.value}</Table.Cell>
                <Table.Cell textAlign="center">
                  {((item.value / totalRecommendations) * 100).toFixed(1)}%
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </>
  );
};

PieGeneric.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired
    })
  ).isRequired,
  handleChartClick: PropTypes.func.isRequired,
  clickType: PropTypes.string,
  graphTextColor: PropTypes.string,
  showDataTables: PropTypes.bool.isRequired,
  styles: PropTypes.shape({})
};

PieGeneric.defaultProps = {
  clickType: "",
  graphTextColor: null,
  styles: {}
};

export default PieGeneric;
